/* eslint-disable react-refresh/only-export-components */
import { type ExternalFile } from '@/lib/types';
import InvisibleInput from '@/components/InvisibleInput';
import type React from 'react';
import { useState } from 'react';
import { type MenuItemProps } from '@mui/material';
import waitForFileSelectorClose from '../../waitForFileSelectorClose';
import { FormattedMessage } from 'react-intl';
import { blockPageClose, unblockPageClose } from '@/lib/pageCloseBlocking';
import useFileSelectorControls from '../../useFileSelectorControls';
import { LARGE_FILE_MAX_SIZE } from '../LargeFileUploader/AbstractLargeFileUploader';
import UploadMenuItem from '../UploadMenuItem';
import UploadMenuItemIcon from '../UploadMenuItem/UploadMenuItemIcon';
import UploadMenuItemContent from '../UploadMenuItem/UploadMenuItemContent';
import UploadProgressMessage from './UploadProgressMessage';
import UploadErrorState from './UploadErrorState';

export type UploadWithExternalDriveProps = Omit<
  MenuItemProps,
  'onClick' | 'disabled'
> & {
  variant: 'google_drive' | 'onedrive';
  onUploadFile: (
    file: File,
    onProgressUpdate: (progress: number) => void,
  ) => Promise<ExternalFile>;
  onAttachUploadedFile: (uploadedFile: ExternalFile) => void;
  onLoadStart: () => [string, string];
  onLoadEnd: (loaderIds: [string, string]) => void;
  onRegisterError: () => [string, string];
  onRemoveError: (errorIds: [string, string]) => void;
};

export enum ErrorType {
  GENERIC,
  FILE_TOO_LARGE,
}

const UploadWithExternalDrive = ({
  variant,
  onUploadFile,
  onAttachUploadedFile,
  onLoadStart,
  onLoadEnd,
  onRegisterError,
  onRemoveError,
  ...props
}: UploadWithExternalDriveProps) => {
  const [inputRef, openFileSelector, resetFileSelectorState] =
    useFileSelectorControls();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [lastAttemptedFile, setLastAttemptedFile] = useState<File | null>(null);

  const [error, setError] = useState<ErrorType | null>(null);
  const [registeredErrorIds, setRegisteredErrorIds] = useState<
    [string, string] | null
  >(null);

  const dismissError = (): void => {
    setError(null);

    if (registeredErrorIds !== null) {
      onRemoveError(registeredErrorIds);
    }
  };

  const uploadFile = async (file: File): Promise<void> => {
    dismissError();

    if (file.size > LARGE_FILE_MAX_SIZE) {
      setError(ErrorType.FILE_TOO_LARGE);
      setRegisteredErrorIds(onRegisterError());

      resetFileSelectorState();

      return;
    }

    setLastAttemptedFile(file);
    setUploading(true);

    const pageCloseBlockerId = blockPageClose();
    const loaderIds = onLoadStart();

    await waitForFileSelectorClose();

    try {
      const newExternalDriveFile = await onUploadFile(
        file,
        (progress: number): void => {
          setUploadProgress(progress);
        },
      );

      onAttachUploadedFile(newExternalDriveFile);

      setLastAttemptedFile(null);
    } catch {
      setError(ErrorType.GENERIC);
      setRegisteredErrorIds(onRegisterError());
    } finally {
      resetFileSelectorState();

      setUploading(false);
      setUploadProgress(null);

      onLoadEnd(loaderIds);
      unblockPageClose(pageCloseBlockerId);
    }
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setLastAttemptedFile(null);
    dismissError();

    const files = e.target.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    uploadFile(file);
  };

  const retryUpload = (): void => {
    if (lastAttemptedFile !== null) {
      uploadFile(lastAttemptedFile);
    }
  };

  return (
    <>
      <InvisibleInput
        type="file"
        onChange={handleFileInputChange}
        ref={inputRef}
      />
      <UploadMenuItem
        onClick={uploading || error !== null ? undefined : openFileSelector}
        disabled={uploading}
        disableRipple={error !== null}
        {...props}
      >
        <UploadMenuItemIcon
          variant={variant}
          uploading={uploading}
          hasError={error !== null}
        />
        <UploadMenuItemContent>
          {uploading ? (
            <UploadProgressMessage progress={uploadProgress} />
          ) : error !== null ? (
            <UploadErrorState
              error={error}
              onRetry={retryUpload}
              canRetry={lastAttemptedFile !== null}
              onDismiss={dismissError}
            />
          ) : (
            <FormattedMessage
              id={`portfolio.challenge.files.add.menu.items.upload_file.options.${variant}`}
            />
          )}
        </UploadMenuItemContent>
      </UploadMenuItem>
    </>
  );
};

export default UploadWithExternalDrive;
